import React, { useCallback, useMemo } from 'react';

import InboxIcon from '@atlaskit/icon/core/inbox';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';
import { useUIStore } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { defineMessage, useIntl } from 'react-intl';

import { type CommonSideNavItemProps } from '@townsquare/griffin-navigation/types';
import { useRouterActions } from '@townsquare/router/primitives/actions';
import { useRouteDependencies } from '@townsquare/router/primitives/dependencies';
import { matchRoute } from '@townsquare/router/primitives/match-route';
import { generatePath } from '@townsquare/router/primitives/path';
import { useRouter } from '@townsquare/router/useRouter';
import { chatRoute, createChatRouteQuery } from '@townsquare/routes/chat';
import { appendSearchStringToPath } from '@townsquare/search-params';

const aiInboxMessage = defineMessage({
	id: 'townsquare.atlassian-home.side-nav.ai-inbox',
	defaultMessage: 'AI Inbox',
	description: 'Side nav button to open the AI Inbox page',
});

export const RovoAiInboxNavItem = (props: CommonSideNavItemProps): React.JSX.Element => {
	const { onClick, item } = props;
	const intl = useIntl();
	const { pushTo } = useRouterActions();
	const [routerStore] = useRouter();
	const { activeMenu, setActiveMenu } = useUIStore();
	const routeDependencies = useRouteDependencies();

	const isOnChatRoute = !!matchRoute([chatRoute], routerStore.location.pathname, undefined);
	const isSelected = isOnChatRoute && activeMenu === 'ai-inbox';

	const aiInboxQuery = useMemo(
		() =>
			createChatRouteQuery(routerStore.query, {
				rovoChatPathway: 'ai-inbox',
			}),
		[routerStore.query],
	);
	// eslint-disable-next-line no-restricted-syntax
	const aiInboxSearchParams = new URLSearchParams(aiInboxQuery);
	const aiInboxHref = appendSearchStringToPath(
		generatePath(chatRoute, routeDependencies),
		aiInboxSearchParams.toString(),
	);

	const onAiInboxClick = useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>) => {
			e.preventDefault();
			pushTo(chatRoute, { query: aiInboxQuery });
			setActiveMenu('ai-inbox');
			onClick(item, e);
		},
		[aiInboxQuery, item, onClick, pushTo, setActiveMenu],
	);

	return (
		<LinkMenuItem
			href={aiInboxHref}
			elemBefore={<InboxIcon label="" color="currentColor" />}
			onClick={onAiInboxClick}
			isSelected={isSelected}
			testId="side-nav-ai-inbox"
		>
			{intl.formatMessage(aiInboxMessage)}
		</LinkMenuItem>
	);
};
